import React from 'react'
import { Loader2, ShieldAlert } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { WorkspaceSummary } from '@/features/onboarding/services/workspaceApi'
import { useCommunicationGuidelines } from '../hooks/useCommunicationGuidelines'
import { CommunicationGuidelinesForm, type CommunicationGuidelinesFormHandle } from './CommunicationGuidelinesForm'

type Props = {
  workspace: WorkspaceSummary
  onDirtyChange?: (dirty: boolean) => void
}

export type CommunicationGuidelinesSectionHandle = CommunicationGuidelinesFormHandle

export const CommunicationGuidelinesSection = React.forwardRef<CommunicationGuidelinesSectionHandle, Props>(
  ({ workspace, onDirtyChange }, ref) => {
    const { currentTheme: t } = useTheme()
    const { record, loading, saving, error, save } = useCommunicationGuidelines(workspace.id)

    const isOwner = workspace.role === 'owner'

    if (loading) {
      return (
        <div className="flex justify-center py-16">
          <Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} />
        </div>
      )
    }

    if (!isOwner) {
      return (
        <div
          className="flex items-start gap-3 rounded-2xl border p-5 text-sm"
          style={{ borderColor: t.border, backgroundColor: t.sectionBackground, color: t.secondrytext }}
        >
          <ShieldAlert size={18} className="mt-0.5 shrink-0" style={{ color: t.warningText }} />
          <p>Only the workspace owner can edit communication guidelines.</p>
        </div>
      )
    }

    return (
      <div className="flex flex-col">
        {error && (
          <div className="mb-4 rounded-xl px-4 py-3 text-sm shrink-0" style={{ backgroundColor: t.errorBackground, color: t.errorText }}>
            {error}
          </div>
        )}
        <CommunicationGuidelinesForm ref={ref} initial={record?.guidelines ?? null} saving={saving} onSave={save} onDirtyChange={onDirtyChange} />
      </div>
    )
  },
)

export default React.memo(CommunicationGuidelinesSection)
